import { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { VideoGenerationMode } from '../state/useGenerationState';
import { RecommendedBadge } from '../../common/RecommendedBadge';
import { HelpTooltip } from '../../common/HelpTooltip';
import styles from './ScenarioGenerator.module.css';

interface VideoFrameModeSelectorProps {
  mode: VideoGenerationMode;
  onModeChange: (mode: VideoGenerationMode) => void;
  disabled?: boolean;
  showBorders?: boolean; // シナリオ生成モード時のみtrueにして上下の区切り線を表示
  isLiteModel?: boolean; // Liteモデル選択時、参考画像タブを無効化
  isPromptMode?: boolean; // プロンプト入力モード時、フレーム推奨表示を外す
  // 「静止画フレームから動画化」カードの中に子要素として出す
  // 「すべてのフレームを自動生成」チェックボックス。state の持ち主は呼び出し側
  // (useScenarioFormState 系) のまま、表示だけここへ寄せる (isPromptMode では未使用)。
  autoGenerateFirstFrame?: boolean;
  onAutoGenerateFirstFrameChange?: (checked: boolean) => void;
  // 同じく「静止画フレームから動画化」カードの中に出す画像生成モデル選択 UI
  // (トリガーボタン + ポータルドロップダウン一式)。creditRatesData 等の取得元は
  // 呼び出し側 (FirstFrameSettingsSection) が持つため、組み立て済み ReactNode を
  // そのまま受け取って表示するだけにする (isPromptMode では未使用)。
  frameModelControl?: React.ReactNode;
  // プロンプト入力モードの 3 タブ目「モーショングラフィックス」を出すか
  // (docs/features/motion-graphics-mode-plan.md §5.1)。判定は呼び出し側
  // (shouldShowMotionGraphicsTab)。isPromptMode でない場合は無視する。
  showMotionTab?: boolean;
  // 3 タブ目の表示条件 (availability) を取得中か。取得中は motion を reference へ寄せない
  // (リロード直後などに motion の選択を失わないため)。isPromptMode でない場合は無視する。
  motionTabPending?: boolean;
}

/** カスタムツールチップコンポーネント（Portal で描画して backdrop-filter の containing block を回避） */
function Tooltip({ text, targetRef, visible }: { text: string; targetRef: React.RefObject<HTMLElement | null>; visible: boolean }) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!visible || !targetRef.current) {
      setPosition(null);
      return;
    }
    const rect = targetRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom + 6,
      left: rect.left + rect.width / 2,
    });
  }, [visible, targetRef]);

  if (!visible || !position) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      style={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        transform: 'translateX(-50%)',
        background: 'rgba(30, 28, 24, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.15)',
        borderRadius: '6px',
        padding: '6px 10px',
        fontSize: '0.72rem',
        color: 'rgba(245, 241, 230, 0.85)',
        whiteSpace: 'nowrap',
        zIndex: 9999,
        pointerEvents: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
      }}
    >
      {text}
    </div>,
    document.body,
  );
}

export const VideoFrameModeSelector = forwardRef<HTMLDivElement, VideoFrameModeSelectorProps>(
  function VideoFrameModeSelector({ mode, onModeChange, disabled = false, showBorders = false, isLiteModel = false, isPromptMode = false, autoGenerateFirstFrame = false, onAutoGenerateFirstFrameChange, frameModelControl, showMotionTab = false, motionTabPending = false }, ref) {
  const className = showBorders
    ? styles.modeSelectorDuringGeneration
    : styles.modeSelectorNoBorders;

  const referenceButtonRef = useRef<HTMLButtonElement>(null);
  // 優先モードカードの「AIおまかせ」カード。Lite モデル時のツールチップ表示先
  const priorityAutoLabelRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const handleReferenceMouseEnter = useCallback(() => {
    if (isLiteModel) setShowTooltip(true);
  }, [isLiteModel]);

  const handleReferenceMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  // Lite選択時に参照画像/ストーリーボードモードだったらフレームモードに強制切り替え
  // (どちらも reference-to-video を使うため Lite では利用不可)
  useEffect(() => {
    if (isLiteModel && (mode === 'reference' || mode === 'storyboard')) {
      onModeChange('frame');
    }
  }, [isLiteModel, mode, onModeChange]);

  // プロンプト入力モードではストーリーボードタブを出さないため、
  // 残留状態で storyboard が選択されていたら参考画像へ寄せる
  useEffect(() => {
    if (isPromptMode && mode === 'storyboard') {
      onModeChange('reference');
    }
  }, [isPromptMode, mode, onModeChange]);

  // 'motion' (モーショングラフィックス) はプロンプト入力モードの 3 タブ目でだけ選べる値。
  // シナリオ生成フォーム、またはタブを出さない条件 (フラグ無し・暫定ゲート) で
  // 残っていたら AIおまかせ (reference) へ寄せる (計画 md §5.1)。availability の取得中は待つ。
  // Lite の制約は motion に掛けない。
  const motionTabVisible = isPromptMode && showMotionTab;
  const motionTabUndecided = isPromptMode && motionTabPending;
  useEffect(() => {
    if (mode === 'motion' && !motionTabVisible && !motionTabUndecided) {
      onModeChange('reference');
    }
  }, [motionTabVisible, motionTabUndecided, mode, onModeChange]);

  // 「シーン単位で制作」内の優先モード (frame/reference) を記憶しておく。大分類を
  // 絵コンテ⇄シーン単位で行き来しても、直前に選んでいた優先モードを失わないため
  // (通常フォームのみで使用。isPromptMode の 2 択フラット UI では参照しない)。
  const lastPerSceneModeRef = useRef<'frame' | 'reference'>(mode === 'reference' ? 'reference' : 'frame');
  useEffect(() => {
    if (mode === 'frame' || mode === 'reference') {
      lastPerSceneModeRef.current = mode;
    }
  }, [mode]);

  // プロンプト入力モードでは参考画像が既定かつ推奨なのでバッジを付ける
  const referenceButton = (
    <div
      style={{ position: 'relative', display: 'flex', flex: 1 }}
      onMouseEnter={handleReferenceMouseEnter}
      onMouseLeave={handleReferenceMouseLeave}
    >
      <button
        ref={referenceButtonRef}
        type="button"
        className={`${styles.modeTabButton} ${mode === 'reference' ? styles.modeTabButtonActive : ''}`}
        onClick={() => !isLiteModel && onModeChange('reference')}
        disabled={disabled || isLiteModel}
        style={isLiteModel ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
      >
        {/* アイコンはシナリオモードの優先モードカード (scene_auto.svg) と揃える */}
        <img src="/scene_auto.svg" alt="" className={styles.modeTabIcon} />
        <span>
          AIおまかせ
          {isPromptMode && !isLiteModel && <RecommendedBadge marginRight={0} marginLeft={4} />}
        </span>
      </button>
      <Tooltip
        text="Liteモデルでは「AIおまかせ」は利用できません"
        targetRef={referenceButtonRef}
        visible={showTooltip}
      />
    </div>
  );

  const frameButton = (
    <button
      type="button"
      className={`${styles.modeTabButton} ${mode === 'frame' ? styles.modeTabButtonActive : ''}`}
      onClick={() => onModeChange('frame')}
      disabled={disabled}
      // ラベルを短縮用に分割しているため、読み上げ名は全文で固定する
      aria-label="静止画フレームから動画化"
    >
      {/* アイコンはシナリオモードの優先モードカード (scene_still_frame.svg) と揃える */}
      <img src="/scene_still_frame.svg" alt="" className={styles.modeTabIcon} />
      {/* 3 タブのモバイル幅では「フレーム」に短縮 (.modeTabButtonsThree の中だけ効く) */}
      <span>
        <span className={styles.modeTabLabelOptional}>静止画</span>フレーム<span className={styles.modeTabLabelOptional}>から動画化</span>
      </span>
    </button>
  );

  // 3 タブ目「モーショングラフィックス」(プロンプト入力モード専用・計画 md §5.1)。
  // レイヤー素材を動かすだけで動画モデルを使わないため、Lite モデルでも無効化しない。
  const motionButton = (
    <button
      type="button"
      className={`${styles.modeTabButton} ${mode === 'motion' ? styles.modeTabButtonActive : ''}`}
      onClick={() => onModeChange('motion')}
      disabled={disabled}
      aria-label="モーショングラフィックス"
    >
      <img src="/scene_motion.svg" alt="" className={styles.modeTabIcon} />
      <span>モーション<span className={styles.modeTabLabelOptional}>グラフィックス</span></span>
    </button>
  );

  // プロンプト入力モード（単発動画生成。シナリオ・production_mode の概念を持たない）は
  // 従来どおりフラットな 2択のまま維持する (docs/features/
  // scene-location-and-production-mode-plan.md §3.8・§6 の対象外)。呼称のみ
  // シナリオモードの優先モードカードに合わせる (静止画フレームから動画化 / AIおまかせ)。
  if (isPromptMode) {
    return (
      <div ref={ref} className={className}>
        <h3 className={styles.modeSelectorTitle}>動画の生成方法を選択してください</h3>
        <div className={styles.modeTabGroup}>
          <div className={`${styles.modeTabButtons} ${motionTabVisible ? styles.modeTabButtonsThree : ''}`}>
            {referenceButton}
            {frameButton}
            {motionTabVisible && motionButton}
          </div>
          <p className={styles.modeTabDescription}>
            {mode === 'motion' && motionTabVisible
              ? 'SVG などレイヤーに分けたデザインを、デザインを変えずに動かします。矢印や効果などの演出は指示で足せます'
              : mode === 'frame'
                ? '動画の最初の1コマ目の静止画を生成してから動画化します。静止画をそのまま動かしたい場合。'
                : isLiteModel
                  ? 'Liteモデルでは「AIおまかせ」は利用できません。StandardまたはFastモデルをお選びください。'
                  : '動画の参考となる画像をいくつか渡して、それを元にAIが自由に動画を創作します。'}
          </p>
        </div>
      </div>
    );
  }

  // シナリオ生成フォーム: 大分類2択（絵コンテから制作 / シーン単位で制作）+
  // 「シーン単位で制作」選択時のみ優先モード（frame/auto）のサブ選択を出す
  // (§3.8 命名・§7 未決事項1: 優先モード選択 UI は参考画像アコーディオンの再編内に置く)。
  const topLevel: 'storyboard' | 'per_scene' = mode === 'storyboard' ? 'storyboard' : 'per_scene';
  const priorityMode: 'frame' | 'auto' = mode === 'reference' ? 'auto' : 'frame';

  const handleTopLevelChange = (next: 'storyboard' | 'per_scene') => {
    if (next === 'storyboard') {
      if (!isLiteModel) onModeChange('storyboard');
    } else {
      onModeChange(lastPerSceneModeRef.current);
    }
  };

  const handlePriorityChange = (next: 'frame' | 'auto') => {
    if (next === 'auto' && isLiteModel) return;
    onModeChange(next === 'auto' ? 'reference' : 'frame');
  };

  return (
    <div ref={ref} className={className}>
      <h3 className={styles.modeSelectorTitle}>動画の生成方法を選択してください</h3>
      <div className={styles.modeTabGroup}>
        <div className={styles.modeTabButtons}>
          <button
            type="button"
            className={`${styles.modeTabButton} ${topLevel === 'storyboard' ? styles.modeTabButtonActive : ''}`}
            onClick={() => handleTopLevelChange('storyboard')}
            disabled={disabled || isLiteModel}
            style={isLiteModel ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
            title={isLiteModel ? 'Liteモデルでは絵コンテから制作は利用できません' : undefined}
          >
            <img src="/video_edit_magic_wand.svg" alt="" className={styles.modeTabIcon} />
            <span>
              絵コンテから制作
              {!isLiteModel && <RecommendedBadge marginRight={0} marginLeft={4} />}
            </span>
          </button>
          <button
            type="button"
            className={`${styles.modeTabButton} ${topLevel === 'per_scene' ? styles.modeTabButtonActive : ''}`}
            onClick={() => handleTopLevelChange('per_scene')}
            disabled={disabled}
          >
            <img src="/scene_auto_stack.svg" alt="" className={styles.modeTabIcon} />
            <span>シーン単位で制作</span>
          </button>
        </div>
        {/* シーン単位で制作の説明は優先モードのヘルプツールチップへ集約 (ユーザー指定)。
            絵コンテ側の説明のみ残す。 */}
        {topLevel === 'storyboard' && (
          <p className={styles.modeTabDescription}>
            {isLiteModel
              ? 'Liteモデルでは絵コンテから制作は利用できません。StandardまたはFastモデルをお選びください。'
              : 'シナリオの絵コンテを丸ごと1枚の静止画として生成してから動画を生成します。'}
          </p>
        )}
      </div>

      {topLevel === 'per_scene' && (
        <div className={styles.modeSubTabGroup}>
          {/* 「優先モード」+「既定の生成方法を選択してください」の2見出しは廃止し、
              1つの見出しに統合 (ユーザー指定)。この見出しが表す設定は、アコーディオン要約の
              「最初のフレーム / ○○」に対応する videoGenerationMode (frame/reference) そのもの
              — 参考画像の利用方法を決める設定は元々ここ1箇所のみで、統合対象の別設定は無い。 */}
          <span className={styles.modeSubTabLabel}>
            デフォルトの参考画像利用方法
            <HelpTooltip ariaLabel="デフォルトの参考画像利用方法のヘルプ">
              <p style={{ margin: 0 }}>
                <strong>静止画フレームから動画化</strong>: 動画の最初の1コマ目の静止画を生成してから動画化します。静止画をそのまま動かしたい場合。
              </p>
              <p style={{ margin: '8px 0 0' }}>
                <strong>参考画像からAIが自由生成（AIおまかせ）</strong>: 動画の参考となる画像をいくつか渡して、それを元にAIが自由に動画を創作します。
              </p>
              <p style={{ margin: '8px 0 0' }}>
                ここで選ぶのはシーンの既定値で、シーンごとに個別に変更することもできます。
              </p>
            </HelpTooltip>
          </span>
          {/* ラジオ+テキストの縦並びから、アイコン付き2カードの排他選択へ (ユーザー指定)。
              カード自体がクリック対象 (role="radio")。フレームカードの中に
              「すべてのフレームを自動生成」+「画像生成モデル」を内包し、非選択時は
              沈めた表示 (priorityCardBodyInactive) にして操作不可にする。 */}
          <div className={styles.priorityCardRow}>
            <div
              role="radio"
              aria-checked={priorityMode === 'frame'}
              tabIndex={disabled ? -1 : 0}
              className={`${styles.priorityCard} ${priorityMode === 'frame' ? styles.priorityCardActive : ''}`}
              onClick={() => !disabled && handlePriorityChange('frame')}
              onKeyDown={(e) => {
                if (disabled) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePriorityChange('frame');
                }
              }}
            >
              <div className={styles.priorityCardHeader}>
                <img src="/scene_still_frame.svg" alt="" className={styles.priorityCardIcon} />
                <span className={styles.priorityCardTitle}>静止画フレームから動画化</span>
              </div>
              <p className={styles.priorityCardDescription}>
                動画の最初の1コマ目の静止画を生成してから動画化します。静止画をそのまま動かしたい場合。
              </p>
              <div className={`${styles.priorityCardBody} ${priorityMode !== 'frame' ? styles.priorityCardBodyInactive : ''}`}>
                {onAutoGenerateFirstFrameChange && (
                  <label className="form-checkbox-label">
                    <input
                      type="checkbox"
                      checked={autoGenerateFirstFrame}
                      onChange={(e) => onAutoGenerateFirstFrameChange(e.target.checked)}
                      disabled={disabled || priorityMode !== 'frame'}
                    />
                    <span>すべてのフレームを自動生成</span>
                  </label>
                )}
                {frameModelControl}
              </div>
            </div>
            <div
              ref={priorityAutoLabelRef}
              role="radio"
              aria-checked={priorityMode === 'auto'}
              tabIndex={disabled || isLiteModel ? -1 : 0}
              className={`${styles.priorityCard} ${priorityMode === 'auto' ? styles.priorityCardActive : ''}`}
              style={isLiteModel ? { opacity: 0.4, cursor: 'not-allowed' } : undefined}
              onClick={() => !disabled && !isLiteModel && handlePriorityChange('auto')}
              onKeyDown={(e) => {
                if (disabled || isLiteModel) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePriorityChange('auto');
                }
              }}
              onMouseEnter={handleReferenceMouseEnter}
              onMouseLeave={handleReferenceMouseLeave}
            >
              <div className={styles.priorityCardHeader}>
                <img src="/scene_auto.svg" alt="" className={styles.priorityCardIcon} />
                <span className={styles.priorityCardTitle}>参考画像からAIが自由生成（AIおまかせ）</span>
              </div>
              <p className={styles.priorityCardDescription}>
                動画の参考となる画像をいくつか渡して、それを元にAIが自由に動画を創作します。
              </p>
              <Tooltip
                text="Liteモデルでは「AIおまかせ」は利用できません"
                targetRef={priorityAutoLabelRef}
                visible={showTooltip}
              />
            </div>
          </div>
          {/* 「シーン単位で制作」の動画は常に BGM なしで生成する (シーンごとに別々の BGM が
              付くと連結時に分断されるため。BGM は BGM セクションで別途 1 本付ける)。
              旧「BGMなし」チェックボックスは 2026-09-24 に撤去し、backend が常時抑止する。 */}
        </div>
      )}
    </div>
  );
});
